import { applyFilterMutation } from 'gqlQueries/filterQueries';
import { getApplyFilterObject } from 'parser/filterParser';
import { client } from 'services/apolloInit';
import { deleteTypeNameFromResponse } from 'utils/helperMethods';
import type { SimulatorFilterCategory } from './SimulatorFilterPanel';
import {
    DEFAULT_SIMULATOR_TABLE_DIMENSIONS,
    SIMULATOR_FILTER_FEATURE_NAME,
    toSimulatorFilterAttributeType,
    type GlobalFilterInclusionItem,
    type SimulatorFilterScopeItem,
} from './simulatorFilter.helpers';

type ApplyFilterDimensionPayload = Record<string, { inclusion?: GlobalFilterInclusionItem[] }>;

type ApplyFilterGraphqlResponse = Record<string, { ids?: Array<number | string> } | undefined>;

function collectSimulatorFilterDimensions(filterCategories: SimulatorFilterCategory[]): string[] {
    const dimensions = new Set<string>(DEFAULT_SIMULATOR_TABLE_DIMENSIONS);
    filterCategories.forEach((category) => {
        if (category.dimension) {
            dimensions.add(category.dimension);
        }
    });
    return [...dimensions];
}

function buildEmptyTableFilters(dimensions: string[]): Record<string, number[]> {
    return Object.fromEntries(dimensions.map((dimension) => [dimension, []]));
}

/** Stages scenario scope + category checkbox selections into global-filter applyFilter shape. */
function buildSimulatorApplyFilterObject(
    filterCategories: SimulatorFilterCategory[],
    categorySelections: Record<string, string[]>,
    appliedScope: SimulatorFilterScopeItem[] = [],
): Record<string, ApplyFilterDimensionPayload> {
    const result: Record<string, ApplyFilterDimensionPayload> = {};

    const pushInclusion = (dimension: string, attributeType: string, attributeExId: string) => {
        if (!result[dimension]) {
            result[dimension] = {};
        }
        if (!result[dimension][attributeType]) {
            result[dimension][attributeType] = { inclusion: [] };
        }
        result[dimension][attributeType].inclusion!.push({
            attributeType,
            attributeDesc: attributeExId,
            attributeExId,
            dimension,
        });
    };

    for (const item of appliedScope) {
        if (!item.attribute_type) continue;
        const dimension = item.dimension ?? 'dim1';
        for (const exId of item.attribute_ex_id ?? []) {
            pushInclusion(dimension, item.attribute_type, String(exId));
        }
    }

    for (const category of filterCategories) {
        const selectedIds = categorySelections[category.key];
        if (!selectedIds?.length) continue;

        const dimension = category.dimension ?? 'dim1';
        const attributeType = category.attributeType ?? toSimulatorFilterAttributeType(category.key);
        for (const selectedId of selectedIds) {
            pushInclusion(dimension, attributeType, selectedId);
        }
    }

    return result;
}

function hasApplyFilterInclusions(applyFilterObject: Record<string, ApplyFilterDimensionPayload>): boolean {
    return Object.values(applyFilterObject).some((dimensionPayload) =>
        Object.values(dimensionPayload).some((attributePayload) => (attributePayload.inclusion?.length ?? 0) > 0),
    );
}

function parseApplyFilterResponseToTableFilters(
    response: ApplyFilterGraphqlResponse,
    dimensions: string[],
): Record<string, number[]> {
    const filters = buildEmptyTableFilters(dimensions);

    for (const dimension of dimensions) {
        const ids = response[dimension]?.ids ?? [];
        filters[dimension] = ids
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id));
    }

    return filters;
}

/** Resolves checkbox labels to numeric dim ids via applyFilter GraphQL (same as global filter). */
export async function resolveSimulatorTableFiltersViaApplyFilter({
    filterCategories,
    categorySelections,
    appliedScope = [],
}: {
    filterCategories: SimulatorFilterCategory[];
    categorySelections: Record<string, string[]>;
    appliedScope?: SimulatorFilterScopeItem[];
}): Promise<Record<string, number[]>> {
    const dimensions = collectSimulatorFilterDimensions(filterCategories);
    const applyFilterObject = buildSimulatorApplyFilterObject(
        filterCategories,
        categorySelections,
        appliedScope,
    );

    if (!hasApplyFilterInclusions(applyFilterObject)) {
        return buildEmptyTableFilters(dimensions);
    }

    const mutationVariables = getApplyFilterObject(applyFilterObject);
    const variables: Record<string, string | undefined> = {
        featureName: SIMULATOR_FILTER_FEATURE_NAME,
    };

    for (const [key, value] of Object.entries(mutationVariables)) {
        variables[key] = JSON.stringify(value);
    }

    const { data } = await client.mutate({
        mutation: applyFilterMutation,
        variables,
    });

    const response = (data ?? {}) as ApplyFilterGraphqlResponse;
    deleteTypeNameFromResponse(response);

    return parseApplyFilterResponseToTableFilters(response, dimensions);
}
